import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Check, MapPin, MessageSquare, Phone } from "lucide-react";
import BlockRenderer from "../components/BlockRenderer";
import ItemCard from "../components/ItemCard";
import Lightbox from "../components/Lightbox";
import SmartImage from "../components/SmartImage";
import Breadcrumbs from "../components/Breadcrumbs";
import { mediaUrl } from "../lib/api";
import { enquireHref } from "../lib/format";
import GalleryRail from "./product/GalleryRail";
import ProcessSteps, { ProcessRail } from "./product/ProcessSteps";
import QuoteForm from "./product/QuoteForm";
import SpecStrip from "./product/SpecStrip";
import VariantCards from "./product/VariantCards";
import { SectionHead, SiblingPills } from "./parts";

/**
 * PRODUCT page - the flagship layout.
 *
 * The migrated source for these pages is a flat wall of paragraphs. The backend
 * parses it into variants / fitment / spec highlights, and this template lays
 * that out as a real product page: content on the left, a sticky quote +
 * gallery rail on the right, so no half of the screen is left empty.
 */
export default function ProductTemplate({
  page,
  structured,
  items = [],
  siblings = [],
  breadcrumbs = [],
  parentLabel,
  phone,
  contact = {},
}) {
  const [lb, setLb] = useState(null);
  const s = structured || {};
  const variants = s.variants || [];
  const highlights = s.highlights || [];

  const photos = useMemo(() => {
    const seen = new Set();
    const out = [];
    [...(page.gallery || []), ...(page.blocks || []).filter((b) => b.type === "image")].forEach(
      (g) => {
        if (g.url && !seen.has(g.url)) {
          seen.add(g.url);
          out.push(g.url);
        }
      }
    );
    return out;
  }, [page]);

  // Some pages (spec-sheet posters, for example) shouldn't be led by an image - the
  // owner wants the copy first and the artwork sitting quietly at the bottom.
  const posterMode = !!page.hide_hero_image;
  const hero = posterMode ? null : page.hero_image || photos[0];
  const railPhotos = posterMode ? photos : photos.filter((p) => p !== hero);
  const heading = page.h1 && page.h1.length < 90 ? page.h1 : page.title;
  // "321 Ingham Rd, Garbutt QLD 4814" -> "Garbutt"
  const suburb = ((contact.address || "").split(",")[1] || "").trim().split(/\s+/)[0] || "";

  const to = enquireHref({
    type: "page",
    id: page.slug,
    title: page.title,
    image: hero,
    category: parentLabel,
  });

  // Photo strip: sits above the copy normally, and below it on poster pages where
  // the artwork is reference material rather than the drawcard.
  const photoStrip = railPhotos.length > 2 ? (
    <section>
      <SectionHead
        eyebrow={posterMode ? "Reference" : "Recent work"}
        title={posterMode ? "Spec sheets & kit options" : `${page.title} builds`}
      />
      <div
        className="scrollbar-thin -mx-1 flex snap-x gap-4 overflow-x-auto px-1 pb-3"
        data-testid="builds-strip"
      >
        {railPhotos.map((url, i) => (
          <button
            key={url}
            type="button"
            onClick={() => setLb(photos.indexOf(url))}
            className={`card-nq shrink-0 snap-start cursor-zoom-in focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--nq-gold)] ${
              posterMode ? "w-[150px] md:w-[170px]" : "w-[240px] md:w-[280px]"
            }`}
            data-testid="builds-strip-item"
            aria-label={`Open photo ${i + 1}`}
          >
            <SmartImage
              src={url}
              alt=""
              ratio={posterMode ? "aspect-[3/4]" : "aspect-[4/3]"}
              fit={posterMode ? "contain" : "cover"}
            />
          </button>
        ))}
      </div>
    </section>
  ) : null;

  return (
    <div data-testid="template-product">
      <div className="container-nq pt-6 md:pt-8">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-10">
          {/* ------------------------------- main ------------------------------- */}
          {/* min-w-0: a grid item defaults to min-width:auto, so the horizontal
              builds rail below was forcing this column to its 7000px content
              width and blowing the whole page out sideways on a phone. */}
          <div className="min-w-0 space-y-12 lg:col-span-8">
            {/* hero */}
            <section data-testid="page-hero">
              <Breadcrumbs items={breadcrumbs} />
              <div className="mt-4 grid gap-6 md:grid-cols-12 md:items-center">
                <div className="md:col-span-7">
                  {parentLabel && <p className="eyebrow">{parentLabel}</p>}
                  <h1
                    className="mt-2 text-3xl font-bold leading-[1.08] text-[color:var(--nq-sand)] md:text-[2.75rem]"
                    data-testid="page-title"
                  >
                    {heading}
                  </h1>
                  {s.intro && (
                    <p className="mt-4 max-w-lg text-sm leading-relaxed text-[color:var(--nq-stone)] md:text-base">
                      {s.intro}
                    </p>
                  )}

                  {highlights.length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3">
                      {highlights.slice(0, 3).map((h) => (
                        <div key={h.label} className="flex items-center gap-2" data-testid="hero-trust-item">
                          <Check className="h-4 w-4 shrink-0 green-text" />
                          <span className="text-xs leading-tight text-[color:var(--nq-stone)]">
                            <span className="block font-semibold text-[color:var(--nq-sand)]">
                              {h.value}
                            </span>
                            {h.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link to={to} className="btn-gold" data-testid="page-enquire-button">
                      <MessageSquare className="h-4 w-4" /> Get a fitted quote
                    </Link>
                    {phone && (
                      <a
                        href={`tel:${phone.replace(/[^\d+]/g, "")}`}
                        className="btn-outline-nq"
                        data-testid="page-call-button"
                      >
                        <Phone className="h-4 w-4" /> {phone}
                      </a>
                    )}
                  </div>

                  <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[color:var(--nq-stone)]">
                    {photos.length > 0 && (
                      <span className="flex items-center gap-1.5" data-testid="hero-photo-count">
                        <Camera className="h-3.5 w-3.5 green-text" /> {photos.length} project photos
                      </span>
                    )}
                    {suburb && (
                      <span className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 green-text" /> {suburb} installation workshop
                      </span>
                    )}
                  </div>
                </div>

                <div className="md:col-span-5">
                  {hero && (
                    <div className="card-nq overflow-hidden">
                      <SmartImage
                        src={hero}
                        alt={heading}
                        ratio="aspect-[4/3]"
                        fit="cover"
                        loading="eager"
                      />
                    </div>
                  )}
                </div>
              </div>
            </section>

            <SpecStrip highlights={highlights} />

            {variants.length > 0 && (
              <section>
                <SectionHead
                  eyebrow="Choose your setup"
                  title={
                    variants.length > 1
                      ? `${variants.length} options to suit your vehicle`
                      : "Your options"
                  }
                />
                <VariantCards
                  variants={variants}
                  images={railPhotos.length ? railPhotos : photos}
                  page={page}
                  parentLabel={parentLabel}
                />
              </section>
            )}

            {items.length > 0 && (
              <section>
                <SectionHead eyebrow="Products" title={`${page.title} we supply`} />
                <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="page-items-grid">
                  {items.map((it) => (
                    <ItemCard key={it.id} item={it} pageTitle={page.title} />
                  ))}
                </div>
              </section>
            )}

            {!posterMode && photoStrip}

            {(s.body || []).length > 0 && (
              <section className="max-w-3xl">
                <BlockRenderer blocks={s.body} />
              </section>
            )}

            {posterMode && photoStrip}

            <ProcessSteps />

            <SiblingPills siblings={siblings} />
          </div>

          {/* ------------------------------ sidebar ----------------------------- */}
          <aside className="min-w-0 lg:col-span-4">
            <div className="space-y-4 lg:sticky lg:top-28">
              {!posterMode && (
                <GalleryRail
                  images={railPhotos.length ? railPhotos : photos}
                  total={photos.length}
                  onOpen={(i) => {
                    const src = (railPhotos.length ? railPhotos : photos)[i];
                    setLb(photos.indexOf(src));
                  }}
                  onViewAll={() => setLb(0)}
                />
              )}
              <ProcessRail />
              <QuoteForm page={page} variants={variants} phone={phone} />
            </div>
          </aside>
        </div>
      </div>

      <Lightbox images={photos} index={lb} onClose={() => setLb(null)} onIndex={setLb} />
    </div>
  );
}
